import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { isAdmin } from '../../features/profile';
import { getRoles, createRole, updateRoleDisplayName, type Role } from '../../features/roles';
import { Card, DashboardPageHeader, DashboardDataTable } from '../../shared/ui';
import AddRoleModal from '../../components/dashboard/AddRoleModal';
import EditRoleModal from '../../components/dashboard/EditRoleModal';

const SYSTEM_ROLE_NAME_ADMIN = 'ADMIN';

const RolesPage: React.FC = () => {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [saving, setSaving] = useState(false);

  const canManage = !!profile && isAdmin(profile);

  const loadRoles = async () => {
    try {
      setIsLoading(true);
      const list = await getRoles();
      setRoles(list);
    } catch (err) {
      console.error(err);
      setError('Failed to load roles');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (canManage) void loadRoles();
  }, [canManage]);

  const handleCreateRole = async (data: { name: string; displayName: string }) => {
    setSaving(true);
    setError(null);
    try {
      await createRole(data);
      await loadRoles();
      showToast('Role created.');
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async (roleId: string, displayName: string) => {
    setSaving(true);
    setError(null);
    try {
      await updateRoleDisplayName(roleId, displayName);
      setEditingRole(null);
      await loadRoles();
      showToast('Role updated.');
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-shield-exclamation me-2" />
            <strong>Access Restricted:</strong> Only administrators can manage roles.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row roles-page">
      <div className="col-12">
        <DashboardPageHeader
          title="Roles"
          description="Define role names used when assigning users and permissions."
          icon="bi bi-shield-lock"
          actions={
            <button type="button" className="btn btn-primary" onClick={() => setShowAddModal(true)}>
              <i className="bi bi-plus-circle me-2" />
              Add Role
            </button>
          }
        />

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </div>
        )}

        <Card title="Existing Roles" icon="bi bi-shield-check">
          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading roles...</p>
            </div>
          ) : roles.length === 0 ? (
            <p className="text-muted mb-0">No roles defined yet.</p>
          ) : (
            <DashboardDataTable aria-label="Existing roles">
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>System Name</th>
                  <th style={{ width: '40%' }}>Display Name</th>
                  <th style={{ width: '20%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                  {roles.map((role) => {
                    const isSystemAdmin = role.name === SYSTEM_ROLE_NAME_ADMIN;
                    return (
                      <tr key={role.id}>
                        <td>
                          <code>{role.name}</code>
                        </td>
                        <td>{role.displayName}</td>
                        <td>
                          {!isSystemAdmin && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              disabled={saving}
                              onClick={() => setEditingRole(role)}
                            >
                              <i className="bi bi-pencil me-1" />
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </DashboardDataTable>
          )}
        </Card>

        <AddRoleModal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          onSubmit={handleCreateRole}
        />
        <EditRoleModal
          show={!!editingRole}
          role={editingRole}
          onHide={() => setEditingRole(null)}
          onSubmit={handleUpdateRole}
        />
      </div>
    </div>
  );
};

export default RolesPage;

